import React, { useState, useEffect } from 'react';
import { CourtPOI, SportType, AthleteProfile } from '../types';
import { X, MapPin, Plus, Camera, Check, ShieldAlert, Search, Sparkles, Navigation, Loader2, ExternalLink } from 'lucide-react';

interface CreateParkModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AthleteProfile;
  onAddCourt: (court: CourtPOI) => void;
}

export const CreateParkModal: React.FC<CreateParkModalProps> = ({
  isOpen,
  onClose,
  user,
  onAddCourt,
}) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [sport, setSport] = useState<SportType>('basketball');
  const [surfaceType, setSurfaceType] = useState('Pro Hard Court');
  const [lighting, setLighting] = useState(true);
  const [isIndoor, setIsIndoor] = useState(false);
  const [rimCondition, setRimCondition] = useState<'Double Rim' | 'Pro Breakaway Glass' | 'Chain Net' | 'Padded Steel' | 'Good'>('Double Rim');
  const [waterFountain, setWaterFountain] = useState(true);
  const [parkingAvailable, setParkingAvailable] = useState(true);
  const [geofenceRadiusMeters, setGeofenceRadiusMeters] = useState(200);
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [imageUrl, setImageUrl] = useState(
    'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&q=80&w=600'
  );
  const [customImage, setCustomImage] = useState<string | null>(null);

  // Geolocation & Auto-Complete Search States
  const [userLat, setUserLat] = useState<number>(40.7589);
  const [userLng, setUserLng] = useState<number>(-73.9851);
  const [isLocating, setIsLocating] = useState(false);
  const [locationSuccessMsg, setLocationSuccessMsg] = useState<string | null>(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchSources, setSearchSources] = useState<any[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [autoFilledNotice, setAutoFilledNotice] = useState<string | null>(null);

  // Auto detect location on modal open
  useEffect(() => {
    if (isOpen && 'geolocation' in navigator) {
      setIsLocating(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude);
          setUserLng(pos.coords.longitude);
          setIsLocating(false);
          setLocationSuccessMsg(`Coords locked: ${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
        },
        (err) => {
          console.warn('Geolocation permission or detection error:', err);
          setIsLocating(false);
        },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFetchLocationManual = () => {
    if (!('geolocation' in navigator)) {
      setSearchError('Geolocation is not supported by your browser.');
      return;
    }
    setIsLocating(true);
    setLocationSuccessMsg(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLat(pos.coords.latitude);
        setUserLng(pos.coords.longitude);
        setIsLocating(false);
        setLocationSuccessMsg(`Location acquired: ${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
      },
      (err) => {
        console.error('Manual location fetch error:', err);
        setIsLocating(false);
        setSearchError('Unable to get exact location. Defaulting to current map bounds.');
      }
    );
  };

  const handleSearchNearbyVenues = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setIsSearching(true);
    setSearchError(null);
    setAutoFilledNotice(null);

    try {
      const res = await fetch('/api/search-parks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: searchQuery.trim(),
          lat: userLat,
          lng: userLng,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to search parks via Google Search AI');
      }

      setSearchResults(data.suggestions || []);
      setSearchSources(data.sources || []);
      if (!data.suggestions || data.suggestions.length === 0) {
        setSearchError('No matching sports parks found. Try searching with a specific park name or city.');
      }
    } catch (err: any) {
      console.error('Park auto-complete error:', err);
      setSearchError(err.message || 'Error executing Google Search grounding.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSuggestion = (venue: any) => {
    setName(venue.name || '');
    setAddress(venue.address || '');
    if (venue.sport) setSport(venue.sport.toLowerCase() as SportType);
    if (venue.surfaceType) setSurfaceType(venue.surfaceType);
    if (typeof venue.isIndoor === 'boolean') setIsIndoor(venue.isIndoor);
    if (typeof venue.lighting === 'boolean') setLighting(venue.lighting);
    if (typeof venue.waterFountain === 'boolean') setWaterFountain(venue.waterFountain);
    if (typeof venue.parkingAvailable === 'boolean') setParkingAvailable(venue.parkingAvailable);
    if (venue.rimCondition) setRimCondition(venue.rimCondition);
    if (venue.lat && venue.lng) {
      setLat(venue.lat);
      setLng(venue.lng);
    }

    setAutoFilledNotice(`Auto-filled details for "${venue.name}"! ✨`);
    setSearchResults([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !address.trim()) return;

    const finalLat = lat !== null ? lat : userLat + (Math.random() - 0.5) * 0.04;
    const finalLng = lng !== null ? lng : userLng + (Math.random() - 0.5) * 0.04;

    const newCourt: CourtPOI = {
      id: `court_${Date.now()}`,
      name: name.trim(),
      address: address.trim(),
      sport,
      lat: finalLat,
      lng: finalLng,
      distanceKm: Number((1.0 + Math.random() * 2.5).toFixed(1)),
      rating: 5.0,
      activePlayersNow: 1,
      lighting,
      isIndoor,
      rimCondition,
      waterFountain,
      parkingAvailable,
      surfaceType,
      hasUpcomingGame: false,
      geofenceRadiusMeters: Number(geofenceRadiusMeters),
      imageUrl: customImage || imageUrl,
      createdBy: user?.id || 'guest',
      createdByName: user?.name || 'Community Athlete',
      status: 'pending', // Starts as pending admin validation
    };

    onAddCourt(newCourt);
    onClose();

    // Reset form
    setName('');
    setAddress('');
    setCustomImage(null);
    setSearchResults([]);
    setAutoFilledNotice(null);
  };

  const presetParkImages = [
    { label: 'Basketball Court', url: 'https://images.unsplash.com/photo-1546519638-68e109498ffc?auto=format&fit=crop&q=80&w=600' },
    { label: 'Baseball / Softball Diamond', url: 'https://images.unsplash.com/photo-1508344928928-7165b67de128?auto=format&fit=crop&q=80&w=600' },
    { label: 'Pickleball Court', url: 'https://images.unsplash.com/photo-1554068865-24cecd4e34b8?auto=format&fit=crop&q=80&w=600' },
    { label: 'Turf Soccer Pitch', url: 'https://images.unsplash.com/photo-1518091043644-c1d4457512c6?auto=format&fit=crop&q=80&w=600' },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto animate-fadeIn">
      <div className="bg-indigo-950 border border-white/20 rounded-[2.5rem] p-6 max-w-lg w-full text-white shadow-2xl my-8 relative space-y-5">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-4">
          <div>
            <div className="flex items-center space-x-2">
              <MapPin className="w-5 h-5 text-lime-400" />
              <h2 className="text-xl font-black italic uppercase tracking-wide">
                Add & Suggest New Park Venue
              </h2>
            </div>
            <p className="text-xs text-indigo-200/70 font-semibold mt-0.5">
              Submit local courts & fields with Google Search grounding. Parks are usable immediately and queued for admin validation.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl bg-indigo-900 hover:bg-indigo-800 text-indigo-300 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Validation Notice Badge */}
        <div className="p-3 bg-amber-400/10 border border-amber-400/30 rounded-2xl flex items-center space-x-3 text-xs">
          <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="text-indigo-200/90 font-medium">
            <span className="font-bold text-amber-300 block">Admin Validation Flow</span>
            Your submission will be tagged as <span className="text-amber-300 font-black">Pending Verification</span>. You and other local players can use it right away!
          </div>
        </div>

        {/* Google Search Auto-Complete & Geolocation Section */}
        <div className="p-4 bg-gradient-to-br from-indigo-900/90 via-purple-950/80 to-indigo-950 border border-lime-400/30 rounded-3xl space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-lime-400" />
              <span className="text-xs font-black uppercase italic tracking-wide text-lime-300">
                Google Search Park Auto-Complete
              </span>
            </div>
            <button
              type="button"
              onClick={handleFetchLocationManual}
              disabled={isLocating}
              className="px-2.5 py-1 bg-indigo-800 hover:bg-indigo-700 text-lime-300 text-[10px] font-bold rounded-lg border border-lime-400/30 flex items-center space-x-1 transition disabled:opacity-50"
            >
              {isLocating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Navigation className="w-3 h-3" />}
              <span>{isLocating ? 'Locating...' : 'Update My Coords'}</span>
            </button>
          </div>

          {locationSuccessMsg && (
            <div className="text-[10px] font-bold text-lime-400 flex items-center space-x-1">
              <Check className="w-3 h-3" />
              <span>{locationSuccessMsg}</span>
            </div>
          )}

          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
              <input
                type="text"
                placeholder="Search venue name (e.g., Westlake Gym, Astoria Pickleball)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearchNearbyVenues();
                  }
                }}
                className="w-full pl-9 pr-3 py-2.5 bg-indigo-950/90 border border-white/10 rounded-xl text-xs text-white placeholder-indigo-300/60 font-semibold outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>
            <button
              type="button"
              onClick={() => handleSearchNearbyVenues()}
              disabled={isSearching}
              className="px-3.5 py-2.5 bg-lime-400 hover:bg-lime-300 text-black text-xs font-black uppercase italic rounded-xl flex items-center space-x-1 transition shrink-0 disabled:opacity-50"
            >
              {isSearching ? (
                <Loader2 className="w-4 h-4 animate-spin text-black" />
              ) : (
                <>
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Search</span>
                </>
              )}
            </button>
          </div>

          {searchError && (
            <p className="text-[11px] font-bold text-rose-400">{searchError}</p>
          )}

          {autoFilledNotice && (
            <div className="p-2.5 bg-lime-400/15 border border-lime-400/40 rounded-xl text-xs font-bold text-lime-300 flex items-center space-x-2">
              <Check className="w-4 h-4 text-lime-400 shrink-0" />
              <span>{autoFilledNotice}</span>
            </div>
          )}

          {/* Search Results Dropdown / List */}
          {searchResults.length > 0 && (
            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-300">
                Click a venue to auto-fill form:
              </p>
              {searchResults.map((v, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSelectSuggestion(v)}
                  className="p-2.5 bg-indigo-950/80 hover:bg-indigo-800/90 border border-white/10 hover:border-lime-400/60 rounded-xl cursor-pointer transition flex items-center justify-between"
                >
                  <div className="space-y-0.5 max-w-[85%]">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-xs text-white line-clamp-1">{v.name}</span>
                      {v.sport && (
                        <span className="px-1.5 py-0.5 bg-lime-400/20 text-lime-300 text-[9px] font-black uppercase rounded">
                          {v.sport}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-indigo-200/70 line-clamp-1">{v.address}</p>
                  </div>
                  <Plus className="w-4 h-4 text-lime-400 shrink-0" />
                </div>
              ))}
            </div>
          )}

          {/* Search Sources/Citations */}
          {searchSources.length > 0 && (
            <div className="pt-1 border-t border-white/10 flex items-center space-x-2 text-[10px] text-indigo-300/80 overflow-x-auto">
              <span className="font-bold text-indigo-200 shrink-0">Sources:</span>
              {searchSources.slice(0, 3).map((src, i) => (
                <a
                  key={i}
                  href={src.uri}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-lime-400 underline flex items-center space-x-0.5 shrink-0"
                >
                  <span className="line-clamp-1 max-w-[120px]">{src.title || 'Google Search'}</span>
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              ))}
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Park Name */}
          <div>
            <label className="text-xs font-bold text-indigo-200 block mb-1">
              Park / Venue Name *
            </label>
            <input
              type="text"
              required
              placeholder="e.g., East River Park Softball Field #3"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full p-3 bg-indigo-900/80 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
            />
          </div>

          {/* Address */}
          <div>
            <label className="text-xs font-bold text-indigo-200 block mb-1">
              Address or Cross Streets *
            </label>
            <input
              type="text"
              required
              placeholder="e.g., E FDR Dr & Grand St, New York, NY"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="w-full p-3 bg-indigo-900/80 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
            />
          </div>

          {/* Primary Sport & Surface */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">
                Primary Sport
              </label>
              <select
                value={sport}
                onChange={(e) => setSport(e.target.value as SportType)}
                className="w-full p-3 bg-indigo-900/80 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
              >
                <option value="basketball">Basketball 🏀</option>
                <option value="baseball">Baseball ⚾</option>
                <option value="softball">Softball 🥎</option>
                <option value="pickleball">Pickleball 🏓</option>
                <option value="soccer">Soccer ⚽</option>
                <option value="volleyball">Volleyball 🏐</option>
                <option value="football">Football 🏈</option>
                <option value="tennis">Tennis 🎾</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">
                Surface Type
              </label>
              <input
                type="text"
                value={surfaceType}
                onChange={(e) => setSurfaceType(e.target.value)}
                placeholder="e.g. Clay Infield, Acrylic Court"
                className="w-full p-3 bg-indigo-900/80 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>
          </div>

          {/* Lighting & Indoor/Outdoor */}
          <div className="grid grid-cols-2 gap-3 items-center">
            <div className="p-3 bg-indigo-900/50 border border-white/10 rounded-xl flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-200">Night Lighting 💡</span>
              <button
                type="button"
                onClick={() => setLighting(!lighting)}
                className={`px-3 py-1 rounded-lg text-xs font-black uppercase italic transition ${
                  lighting ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-300'
                }`}
              >
                {lighting ? 'Yes' : 'No'}
              </button>
            </div>

            <div className="p-3 bg-indigo-900/50 border border-white/10 rounded-xl flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-200">Location Type</span>
              <button
                type="button"
                onClick={() => setIsIndoor(!isIndoor)}
                className={`px-3 py-1 rounded-lg text-xs font-black uppercase italic transition ${
                  isIndoor ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-300'
                }`}
              >
                {isIndoor ? 'Indoor 🏢' : 'Outdoor 🌳'}
              </button>
            </div>
          </div>

          {/* Rim Condition & Geo-Fence */}
          <div className="grid grid-cols-2 gap-3 items-center">
            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">
                Rim & Net Condition
              </label>
              <select
                value={rimCondition}
                onChange={(e) => setRimCondition(e.target.value as any)}
                className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl text-xs font-bold text-white outline-none focus:ring-2 focus:ring-lime-400"
              >
                <option value="Double Rim">Double Rim</option>
                <option value="Pro Breakaway Glass">Pro Breakaway Glass</option>
                <option value="Chain Net">Chain Net</option>
                <option value="Padded Steel">Padded Steel</option>
                <option value="Good">Good / Standard</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-indigo-200 block mb-1">
                Geo-Fence Radius (m)
              </label>
              <input
                type="number"
                min="50"
                max="1000"
                value={geofenceRadiusMeters}
                onChange={(e) => setGeofenceRadiusMeters(Number(e.target.value))}
                className="w-full p-2.5 bg-indigo-900/80 border border-white/10 rounded-xl text-xs font-semibold text-white outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>
          </div>

          {/* Amenities Toggles */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-indigo-900/50 border border-white/10 rounded-xl flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-200">Water Fountain 🚰</span>
              <button
                type="button"
                onClick={() => setWaterFountain(!waterFountain)}
                className={`px-3 py-1 rounded-lg text-xs font-black uppercase italic transition ${
                  waterFountain ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-300'
                }`}
              >
                {waterFountain ? 'Yes' : 'No'}
              </button>
            </div>

            <div className="p-3 bg-indigo-900/50 border border-white/10 rounded-xl flex items-center justify-between">
              <span className="text-xs font-bold text-indigo-200">Parking Lot 🅿️</span>
              <button
                type="button"
                onClick={() => setParkingAvailable(!parkingAvailable)}
                className={`px-3 py-1 rounded-lg text-xs font-black uppercase italic transition ${
                  parkingAvailable ? 'bg-lime-400 text-black' : 'bg-indigo-950 text-indigo-300'
                }`}
              >
                {parkingAvailable ? 'Yes' : 'No'}
              </button>
            </div>
          </div>

          {/* Park Photo Upload / Preset Selection */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-indigo-200 block">
              Park Photo Image
            </label>

            <div className="flex items-center space-x-3">
              <div className="w-20 h-16 rounded-xl overflow-hidden border border-lime-400 shrink-0">
                <img
                  src={customImage || imageUrl}
                  alt="Park Preview"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>

              <div className="flex-1 space-y-1.5">
                <label className="cursor-pointer inline-flex items-center space-x-2 px-3 py-2 bg-indigo-900 hover:bg-indigo-800 text-lime-400 border border-lime-400/40 rounded-xl text-xs font-black italic uppercase transition">
                  <Camera className="w-4 h-4" />
                  <span>Upload Park Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          if (event.target?.result) {
                            setCustomImage(event.target.result as string);
                          }
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
                <p className="text-[10px] text-indigo-200/60 font-semibold">
                  Or pick a photo template below:
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1">
              {presetParkImages.map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    setCustomImage(null);
                    setImageUrl(preset.url);
                  }}
                  className={`p-1.5 rounded-xl border text-[10px] font-bold text-left flex items-center space-x-2 transition ${
                    !customImage && imageUrl === preset.url
                      ? 'border-lime-400 bg-lime-400/10 text-lime-300'
                      : 'border-white/10 bg-indigo-900/50 text-indigo-200 hover:text-white'
                  }`}
                >
                  <img src={preset.url} alt={preset.label} className="w-8 h-8 rounded-lg object-cover shrink-0" referrerPolicy="no-referrer" />
                  <span className="line-clamp-1">{preset.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Submit Action */}
          <button
            type="submit"
            className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-sm rounded-2xl shadow-xl shadow-lime-400/20 flex items-center justify-center space-x-2 transition mt-2"
          >
            <Plus className="w-5 h-5 stroke-[3]" />
            <span>Add Park to Platform</span>
          </button>
        </form>
      </div>
    </div>
  );
};

